import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDocumentUpload } from 'Core/hooks';
import CustomPropTypes from 'CustomPropTypes';
import { TextEditor } from 'Core/components/editor';
import { ZoomableImageModal } from 'Core/components';
import { downloadImage } from 'Core/utils';
 
const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '15px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  header: {
    marginBottom: '15px',
    color: '#333',
    fontSize: '14px',
    fontWeight: '500',
  },
  editorContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    marginBottom: '15px',
    backgroundColor: '#fff',
  },
  toolbar: {
    display: 'flex',
    padding: '6px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    alignItems: 'center',
    gap: '2px',
  },
  toolButton: {
    padding: '4px 6px',
    border: '1px solid #e5e7eb',
    background: 'white',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#666',
  },
  select: {
    padding: '4px 6px',
    border: '1px solid #e5e7eb',
    borderRadius: '3px',
    backgroundColor: 'white',
    marginRight: '6px',
    fontSize: '12px',
    color: '#666',
  },
  editor: {
    padding: '8px',
    minHeight: '80px',
    width: '100%',
    border: 'none',
    resize: 'none',
    outline: 'none',
    fontSize: '13px',
  },
  fileSection: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    borderTop: '1px solid #e5e7eb',
    gap: '8px',
  },
  chooseFile: {
    padding: '6px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: '3px',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#666',
  },
  noFile: {
    color: '#666',
    flex: 1,
    fontSize: '12px',
  },
  addButton: {
    backgroundColor: '#7bb13c',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  row: {
    backgroundColor: '#f9fafb',
    marginBottom: '4px',
    padding: '8px 10px',
    borderRadius: '3px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move',
    border: '1px solid #e5e7eb',
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  rowText: {
    fontSize: '13px',
    color: '#333',
  },
  previewImage: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '3px',
    border: '1px solid #e5e7eb',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  actionLink: {
    color: '#2196f3',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '4px',
  },
  removeLink: {
    color: '#ff5252',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '4px',
  },
  moveButton: {
    backgroundColor: '#e5e7eb',
    color: '#666',
    border: 'none',
    borderRadius: '3px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '12px',
    marginLeft: '4px',
  },
  editContainer: {
    backgroundColor: '#f0f8ff',
    border: '2px solid #2196f3',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '8px',
  },
  editInput: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    fontSize: '13px',
    marginBottom: '8px',
  },
  editFileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  editButtons: {
    display: 'flex',
    gap: '8px',
  },
  saveButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '12px',
  },
};

const initData = {
  id: 0,
  inspectionPlanId: 0,
  inspectionPlan: '',
  fileName: '',
  filePath: '',
  relativePath: '',
  stepNumber: 0,
};
// Component implementation remains the same as before, just the styles have been updated
const InspectionSteps = ({ data, onChanges }) => {
  const [stepInputs, setStepInputs] = useState(initData);
  const [steps, setSteps] = useState(data);

  const [items, setItems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(data);
  const [noteText, setNoteText] = useState('');
  const fileInputRef = useRef(null);
  const { uploadDocuments, loading } = useDocumentUpload();
  
  // State for zoomable image modal
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // State for edit functionality
  const [editingItemId, setEditingItemId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editFile, setEditFile] = useState(null);

  const updateStepField = (field, value) => {
    setStepInputs(prevStepInputs => ({
      ...prevStepInputs,
      [field]: value,
    }));
  };

  const removePart = id => {
    const newSteps = steps.filter(step => step.id !== id);
    setSteps(newSteps);
    if (onChanges) onChanges(newSteps);
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleAdd = async event => {
    if (noteText.trim()) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('UploadCategory', 'Inspection-SystemRequirement');

      const result = await uploadDocuments(formData);
      if (result) {
        const newStep = {
          id: Math.floor(Math.random() * 101),
          fileName: result.fileName,
          filePath: result.filePath,
          relativePath: result.relativePath,
          inspectionPlan: noteText,
          stepNumber:(steps.length+1)
        };
        const newSteps = [...steps, newStep];
        const updatedSteps = updateStepNumbers(newSteps);
        setSteps(updatedSteps);
        setStepInputs(initData);
        setNoteText('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
          if (onChanges) onChanges(updatedSteps);
        }
      }
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = Number(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;
    const newSteps = [...steps];
    const [draggedItem] = newSteps.splice(dragIndex, 1);
    newSteps.splice(dropIndex, 0, draggedItem);

    const updatedSteps = updateStepNumbers(newSteps);
    setSteps(updatedSteps);
  };

  const updateStepNumbers = (stepsArray) => {
    return stepsArray.map((step, index) => ({
      ...step,
      stepNumber: index + 1
    }));
  };

  const handleRemove = id => {
    const newSteps = steps.filter(step => step.id !== id);
    const updatedSteps = updateStepNumbers(newSteps);
    setSteps(updatedSteps);
    if (onChanges) onChanges(updatedSteps);
  };

  const handleMove = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < steps.length) {
      const newSteps = [...steps];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      const updatedSteps = updateStepNumbers(newSteps);
      
      setSteps(updatedSteps);
      if (onChanges) onChanges(updatedSteps);
    }
  };

  // Handler for opening zoom modal
  const handleImageClick = (e, item) => {
    e.stopPropagation();
    setSelectedImage(item);
    setShowZoomModal(true);
  };

  // Handler for closing zoom modal
  const handleCloseZoomModal = () => {
    setShowZoomModal(false);
    setSelectedImage(null);
  };

  // Handler for download from zoom modal
  const handleZoomModalDownload = (imageSrc) => {
    downloadImage(imageSrc);
  };

  // Handler for starting edit mode
  const handleStartEdit = (item) => {
    setEditingItemId(item.id);
    setEditText(item.inspectionPlan ? item.inspectionPlan.replace(/<[^>]*>/g, '') : '');
    setEditFile(null);
  };

  // Handler for canceling edit
  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditText('');
    setEditFile(null);
  };

  // Handler for saving edit
  const handleSaveEdit = async () => {
    if (!editText.trim()) return;

    const itemIndex = steps.findIndex(step => step.id === editingItemId);
    if (itemIndex === -1) return;

    let updatedItem = { ...steps[itemIndex], inspectionPlan: editText };

    // If a new file was selected, upload it
    if (editFile) {
      const formData = new FormData();
      formData.append('file', editFile);
      formData.append('UploadCategory', 'Inspection-SystemRequirement');

      const result = await uploadDocuments(formData);
      if (result) {
        updatedItem = {
          ...updatedItem,
          fileName: result.fileName,
          filePath: result.filePath,
          relativePath: result.relativePath,
        };
      }
    }

    const newSteps = [...steps];
    newSteps[itemIndex] = updatedItem;
    
    setSteps(newSteps);
    if (onChanges) onChanges(newSteps);
    
    // Reset edit state
    setEditingItemId(null);
    setEditText('');
    setEditFile(null);
  };

  // Handler for file change during edit
  const handleEditFileChange = (event) => {
    const file = event.target.files[0];
    setEditFile(file);
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.header}>Notes</h3>

      <div style={styles.editorContainer}>
        <TextEditor
          style={styles.editor}
          data={noteText}
          onChange={val => setNoteText(val)}
          placeholder="Enter notes..."
        />

        <div style={styles.fileSection}>
          <label style={styles.chooseFile}>
            Choose File
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
            />
          </label>
          <span style={styles.noFile}>{selectedFile ? selectedFile.name : 'No file chosen'}</span>
          <button style={styles.addButton} onClick={handleAdd}>
            Add
          </button>
        </div>
      </div>

      {steps.map((item, index) => (
        <div key={item.id}>
          {editingItemId === item.id ? (
            // Edit Mode
            <div style={styles.editContainer}>
              <input
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                style={styles.editInput}
                placeholder="Enter inspection plan..."
              />
              <div style={styles.editFileSection}>
                <label style={styles.chooseFile}>
                  Choose New File (Optional)
                  <input
                    type="file"
                    hidden
                    onChange={handleEditFileChange}
                    accept="image/*"
                  />
                </label>
                <span style={styles.noFile}>
                  {editFile ? editFile.name : 'No new file selected'}
                </span>
              </div>
              <div style={styles.editButtons}>
                <button style={styles.saveButton} onClick={handleSaveEdit}>
                  Save
                </button>
                <button style={styles.cancelButton} onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => e.preventDefault()}
              onDrop={e => handleDrop(e, index)}
              style={styles.row}
            >
              <div className="col-md-8">
                <div className="row">
                  <div className="col-md-10">
                    <div style={styles.rowText}>
                      {item.inspectionPlan ? item.inspectionPlan.replace(/<[^>]*>/g, '') : ''}
                    </div>
                  </div>
                  <div className="col-md-2">
                    {item.fileName && (
                      <img 
                        src={item.relativePath} 
                        alt="Preview" 
                        style={{...styles.previewImage, cursor: 'pointer'}}
                        onClick={e => handleImageClick(e, item)}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-2" style={styles.actions}>
                <span style={styles.actionLink} onClick={() => handleStartEdit(item)}>
                  Edit
                </span>
                <span style={styles.removeLink} onClick={() => handleRemove(item.id)}>
                  Remove
                </span>
                <button
                  style={styles.moveButton}
                  onClick={() => handleMove(index, 'up')}
                  disabled={index === 0}
                >
                  ↑
                </button>
                <button
                  style={styles.moveButton}
                  onClick={() => handleMove(index, 'down')}
                  disabled={index === steps.length - 1}
                >
                  ↓
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
      
      {/* Zoomable Image Modal with Editor */}
      <ZoomableImageModal
        show={showZoomModal}
        onHide={handleCloseZoomModal}
        imageSrc={selectedImage?.relativePath}
        imageAlt={selectedImage?.fileName || 'Inspection Step Image'}
        onDownload={handleZoomModalDownload}
        enableEditor={true} // Set to false to hide editor for specific users
        onSaveEdited={(blob) => {
          // Optional: Handle the edited image blob (e.g., upload to server)
          console.log('Edited image saved:', blob);
        }}
      />
    </div>
  );
};
InspectionSteps.defaultProps = {
  onChanges: () => {},
  data: [],
};

InspectionSteps.propTypes = {
  onChanges: PropTypes.func,
  data: CustomPropTypes.Files,
};

export default InspectionSteps;
